import { Component, Host, h, Prop, State } from '@stencil/core';
import Star2 from "../../assets/svg/star2.svg";
import type { ReviewsSummary } from './interfaces';

@Component({
  tag: 'salla-reviews-summary',
  styleUrl: 'salla-reviews-summary.scss'
})
export class SallaReviewsSummary {

  @State() data: ReviewsSummary;
  /**
   * Product ID
   */
  @Prop() itemId!: number;
  @State() canRender = false;
  @State() basedOnLabel: string;
  @State() recommendedLabel: string;
  @State() showRatingSummary = false;
  @State() recommendationOnly = false;
  @State() showRecommendation = false;

  async componentWillLoad() {
    try {
      await salla.onReady();
      this.initializeLanguages()
      this.loadConfig();

      // Only load data if component should be displayed
      if (this.showRatingSummary || this.showRecommendation) {
        await this.loadSummary();
      }
    } catch (error) {
      console.error('Failed to initialize reviews summary component:', error);
    }
  }


  private async loadConfig() {
    this.showRatingSummary = salla.config.get('store.settings.rating.show_rating_summary');
    this.showRecommendation = salla.config.get('store.settings.rating.show_recommendation');
    this.recommendationOnly = this.showRecommendation && !this.showRatingSummary;
  }

  private initializeLanguages() {
    return Salla.lang.onLoaded(() => {
      Salla.lang.addBulk({
        "blocks.comments.based_on": { ar: "بناءً على", en: "Based on" },
        "blocks.comments.recommended": { ar: "أوصوا بالمنتج", en: "Recommended" }
      })
      this.basedOnLabel = salla.lang.get('blocks.comments.based_on');
      this.recommendedLabel = salla.lang.get('blocks.comments.recommended');
    }
    );
  }

  private async loadSummary() {
    if(!this.itemId){
      console.error('Error loading reviews summary: itemId is not defined');
      return this.canRender = false;
    }

    try {
      const response = (await salla.api.request(`rating/summary/${this.itemId}`)) as { data: ReviewsSummary } | undefined;

      if (!response?.data || (Array.isArray(response.data.reviews) && !response.data.reviews.length)) {
        this.canRender = false;
        this.data = null;
        return;
      }

      this.data = response.data;
      this.canRender = true;

    } catch (error) {
      this.canRender = false;
      this.data = null;
      console.error('Error loading reviews summary:', error);
    }
  }
  private renderRecommendation() {
    if (!this.showRecommendation || !this.data?.recommendation) {
      return null;
    }

    const classes = {
      base: 's-reviews-summary-header-section',
      standalone: this.recommendationOnly ? 's-reviews-summary-recommendation-only' : ''
    };

    return (
      <div class={`${classes.base} ${classes.standalone}`.trim()}>
        <h4 class="s-reviews-summary-recommendation-percentage">%{this.data?.recommendation}</h4>
        <p class="s-reviews-summary-count">{this.recommendedLabel}</p>
      </div>
    );
  }


  render() {
    if (!this.canRender) return null;

    if ( this.recommendationOnly)
      return this.renderRecommendation()

    const ratings = {
      "1": 0,
      "2": 0,
      "3": 0,
      "4": 0,
      "5": 0,
      ...this.data.reviews
    };


    const reviewElements = Object.keys(ratings).reverse().map((rating) => {
      const percentage = ratings[rating];
      return (
        <div key={rating} class="s-reviews-summary-row">
          <div class="s-reviews-summary-row-rate">{rating} <span innerHTML={Star2} /></div>
          <salla-progress-bar class="s-reviews-summary-progress" value={percentage} target={100} hideUnits={true} height='16px' />
          <span class="s-reviews-summary-percentage">{percentage}%</span>
        </div>
      );
    });

    return (
      <Host class="s-reviews-summary-wrapper">
        <div class="s-reviews-summary-header">
          <div class={"s-reviews-summary-header-section"}>
            <h3 class="s-reviews-summary-average">{this.data.rating}</h3>
            <div>
              <salla-rating-stars size='large' value={this.data.rating} />
              <p class="s-reviews-summary-count">{this.basedOnLabel} {salla.helpers.number(salla.lang.choice('pages.rating.reviews', this.data.count))}</p>
            </div>
          </div>
          {this.renderRecommendation()}
        </div>
        <div class="s-reviews-summary-rows">
          {reviewElements}
        </div>
      </Host>
    );
  }
}



